/**
 * Invited employee, step 1: "Create your password." (design: ob-admin.jsx
 * EmpPassword). Continue accepts the invitation and signs in.
 */
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { ArrowRight } from "lucide-react";
import {
  Btn,
  Field,
  ObMain,
  ObShell,
  PasswordCard,
  passwordReady,
  pwNameParts,
  type InvitationErrorBody,
  type InvitationValidation,
} from "../kit";
import { useShellStore } from "../../../store";
import { AccountErrorNote, InviteeTopBar, orgDisplayName } from "./common";
import { createAccountAndSignIn, type CreateAccountOutcome } from "./session";

export interface MemberPasswordScreenProps {
  token: string;
  invitation: InvitationValidation;
  onLinkProblem: (code: string, body: InvitationErrorBody | null) => void;
  onSignInNeeded: () => void;
  /** Account created and signed in; `name` is the name used (for the tour greeting). */
  onAccountReady: (name: string) => void;
}

export function MemberPasswordScreen({ token, invitation, onLinkProblem, onSignInNeeded, onAccountReady }: MemberPasswordScreenProps) {
  const { t } = useTranslation();
  const setUser = useShellStore((s) => s.setUser);
  const org = orgDisplayName(invitation.organization, invitation.organization_name);
  const minLength = invitation.password_policy?.min_length ?? 12;
  const needsName = !(invitation.name ?? "").trim();

  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);
  const [pwErrors, setPwErrors] = useState<string[] | null>(null);
  const [acctErr, setAcctErr] = useState<Extract<CreateAccountOutcome, { status: "error" }> | null>(null);

  const personName = invitation.name || name;
  const nameParts = pwNameParts(personName, invitation.login_email, invitation.organization?.name);
  const ok = passwordReady(pw, pw2, nameParts, minLength) && (!needsName || !!name.trim());

  const submit = async () => {
    if (!ok || busy) return;
    setBusy(true);
    setPwErrors(null);
    setAcctErr(null);
    try {
      const outcome = await createAccountAndSignIn({
        token,
        password: pw,
        name: needsName ? name : undefined,
        loginEmail: invitation.login_email,
        setUser,
      });
      switch (outcome.status) {
        case "signed-in":
          onAccountReady(personName.trim());
          return;
        case "created":
          onSignInNeeded();
          return;
        case "password":
          setPwErrors(outcome.errors.length ? outcome.errors : [t("onboarding.invitee.err.password", "Choose a different password.")]);
          return;
        case "link":
          onLinkProblem(outcome.code, outcome.body);
          return;
        default:
          setAcctErr(outcome);
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <ObShell>
      <InviteeTopBar orgName={org} personName={personName.trim() || null} />
      <ObMain width={560}>
        <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 16, padding: "0 4px" }} data-screen-label="Employee set password">
          <p className="ob-eye">
            {org
              ? t("onboarding.invitee.member.eyebrow", { defaultValue: "Welcome to {{org}}", org })
              : t("onboarding.invitee.member.eyebrowNoOrg", "Welcome to We-Co")}
          </p>
          <h1 className="ob-h1" style={{ fontSize: 30 }}>
            {t("onboarding.invitee.member.title", "Create your password.")}
          </h1>
        </div>
        {acctErr && (
          <div style={{ marginBottom: 16 }}>
            <AccountErrorNote outcome={acctErr} />
          </div>
        )}
        {needsName && (
          <section className="ob-glass" style={{ padding: "clamp(20px,3cqi,28px)", marginBottom: 16 }}>
            <Field label={t("onboarding.invitee.yourName", "Your full name")}>
              <input className="ob-in" value={name} onChange={(e) => setName(e.target.value)} autoComplete="name" />
            </Field>
          </section>
        )}
        <PasswordCard
          value={pw}
          onChange={(v) => {
            setPw(v);
            setPwErrors(null);
          }}
          confirm={pw2}
          onConfirmChange={setPw2}
          email={invitation.login_email}
          nameParts={nameParts}
          minLength={minLength}
          serverErrors={pwErrors}
          onSubmit={() => void submit()}
        />
        <div style={{ marginTop: 16, display: "flex", justifyContent: "flex-end" }}>
          <Btn size="l" disabled={!ok} busy={busy} onClick={() => void submit()} iconEnd={ArrowRight} flipEnd>
            {t("onboarding.kit.continue", "Continue")}
          </Btn>
        </div>
      </ObMain>
    </ObShell>
  );
}
